import React, { useEffect, useMemo, useState } from 'react';
import {
    Container,
    Dropdown,
    IconButton,
    Menu,
    MenuButton,
    MenuItem,
    Stack,
    Tab,
    TabList,
    Tabs,
    Table,
    Typography,
    Button,
} from '@mui/joy';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import EditIcon from '@mui/icons-material/Edit';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import Star from '@mui/icons-material/Star';
import StarBorder from '@mui/icons-material/StarBorder';
import { useLoaderData, useNavigate } from 'react-router';
import { CourseSummary } from '../models/course';
import { useAuth } from '../auth/AuthContext';
import { serviceFacade } from '../services/service-facade';

interface CourseListLoaderData {
    courses: CourseSummary[];
}

type CourseScope = 'owned' | 'favorites' | 'all';

const EMPTY_MESSAGE: Record<CourseScope, string> = {
    owned: "You don't own any courses yet.",
    favorites: "You haven't favorited any courses yet.",
    all: 'No courses yet.',
};

const CourseList = () => {
    const { courses } = useLoaderData() as CourseListLoaderData;
    const navigate = useNavigate();
    const { userId, playerId } = useAuth();
    const [copyingId, setCopyingId] = useState<string | null>(null);
    const [favoriteIds, setFavoriteIds] = useState<string[]>([]);
    const [scope, setScope] = useState<CourseScope>('owned');

    useEffect(() => {
        if (!playerId) return;
        serviceFacade
            .getFavoriteCourseIds(playerId)
            .then(setFavoriteIds)
            .catch(() => {});
    }, [playerId]);

    const displayedCourses = useMemo(() => {
        switch (scope) {
            case 'owned':
                return courses.filter((course) => course.ownerPlayerId === playerId);
            case 'favorites':
                return courses.filter((course) => favoriteIds.includes(course.id));
            case 'all':
                return courses;
        }
    }, [courses, scope, favoriteIds, playerId]);

    const handleCopy = async (courseId: string) => {
        if (!userId || !playerId) {
            navigate('/login');
            return;
        }

        try {
            setCopyingId(courseId);
            const copy = await serviceFacade.copyCourse(courseId, playerId);
            navigate(`/courses/${copy.id}`);
        } catch (error) {
            console.error('Error copying course:', error);
        } finally {
            setCopyingId(null);
        }
    };

    const handleToggleFavorite = async (courseId: string) => {
        if (!userId || !playerId) {
            navigate('/login');
            return;
        }

        try {
            const isFavorited = favoriteIds.includes(courseId);
            const updated = isFavorited
                ? await serviceFacade.unfavoriteCourse(courseId, playerId)
                : await serviceFacade.favoriteCourse(courseId, playerId);
            setFavoriteIds(updated);
        } catch (error) {
            console.error('Error toggling favorite:', error);
        }
    };

    return (
        <Container>
            <Stack spacing={3}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography level="h2">Courses</Typography>
                    <Button onClick={() => navigate('/courses/new')}>New Course</Button>
                </Stack>

                <Tabs value={scope} onChange={(_, value) => setScope(value as CourseScope)}>
                    <TabList>
                        <Tab value="owned">My Courses</Tab>
                        <Tab value="favorites">Favorites</Tab>
                        <Tab value="all">All</Tab>
                    </TabList>
                </Tabs>

                {displayedCourses.length === 0 ? (
                    <Typography>{EMPTY_MESSAGE[scope]}</Typography>
                ) : (
                    <Table>
                        <thead>
                            <tr>
                                <th>Name</th>
                                <th>Holes</th>
                                <th>Par</th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {displayedCourses.map((course) => (
                                <tr
                                    key={course.id}
                                    onClick={() => navigate(`/courses/${course.id}`)}
                                    style={{ cursor: 'pointer' }}
                                >
                                    <td>{course.name}</td>
                                    <td>{course.holeCount}</td>
                                    <td>{course.totalPar}</td>
                                    <td onClick={(e) => e.stopPropagation()}>
                                        <Dropdown>
                                            <MenuButton
                                                aria-label="Course actions"
                                                slots={{ root: IconButton }}
                                                slotProps={{ root: { variant: 'plain', color: 'neutral', size: 'sm' } }}
                                                disabled={copyingId === course.id}
                                            >
                                                <MoreVertIcon />
                                            </MenuButton>
                                            <Menu placement="bottom-end">
                                                {course.ownerPlayerId === playerId && (
                                                    <MenuItem onClick={() => navigate(`/courses/${course.id}/edit`)}>
                                                        <EditIcon fontSize="small" />
                                                        &nbsp;Edit
                                                    </MenuItem>
                                                )}
                                                <MenuItem
                                                    onClick={() => handleCopy(course.id)}
                                                    disabled={copyingId === course.id}
                                                >
                                                    <ContentCopyIcon fontSize="small" />
                                                    &nbsp;Copy
                                                </MenuItem>
                                                <MenuItem onClick={() => handleToggleFavorite(course.id)}>
                                                    {favoriteIds.includes(course.id) ? (
                                                        <>
                                                            <Star fontSize="small" />
                                                            &nbsp;Unfavorite
                                                        </>
                                                    ) : (
                                                        <>
                                                            <StarBorder fontSize="small" />
                                                            &nbsp;Favorite
                                                        </>
                                                    )}
                                                </MenuItem>
                                            </Menu>
                                        </Dropdown>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                )}
            </Stack>
        </Container>
    );
};

export default CourseList;
